import { useEffect, useRef, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { AuthCard } from '../components/auth/AuthCard';
import { AuthShell } from '../components/auth/AuthShell';
import { log } from '@/lib/logger';
import { safeRedirect } from '../lib/utils';
import { capture } from '../lib/analytics';
import { consumeAttribution } from '../lib/attribution';
import { captureFreeScanClaimParams } from '../lib/freeScanClaim';
import { captureFreeToolSite } from '../lib/freeToolSite';
import { captureSiteAuditClaimParams } from '@/lib/siteAuditClaim';
import { useSiteAuditPreview } from '../hooks/useSiteAuditPreview';
import { GOOGLE_UNFINISHED_PARAM } from './auth-callback-outcome';
import { consumePendingCallback, sessionStorageOrNull, type LandedCallback } from '../lib/authCallbackPending';
import { failDashboardOAuthAttempt } from '../lib/dashboardAuthTelemetry';

export function SignInPage() {
  const { isSignedIn, isLoaded } = useAuth();
  const [params] = useSearchParams();
  const [returnedCallback, setReturnedCallback] = useState<LandedCallback | null>(null);
  const redirectUrl = safeRedirect(params.get('redirect_url')) || safeRedirect(returnedCallback?.redirect_target ?? null);
  const initialEmail = params.get('email') || '';
  // InvitePage sends people here with the invitee's address prefilled and the
  // invitation as the destination (its signed-out CTAs, and the "Switch
  // account" sign-out from a mismatched account, 2026-10-09). Say what the
  // prefilled address is FOR. The team name is deliberately not read off the
  // query string: it would render attacker-chosen text as a trusted team.
  const inviteDestination = Boolean(redirectUrl?.startsWith('/invite/')) && Boolean(initialEmail);
  // Set by the App-level session-expired listener (useSessionExpiredRedirect)
  // when a 401 with no live Clerk session bounced the user here.
  const sessionExpired = params.get('expired') === '1';
  // Set by AuthCallbackPage when a Google attempt never formed a session (the
  // bot check refused it, or never completed). The email form below takes the
  // same person without a Google round trip.
  const googleUnfinished = params.get(GOOGLE_UNFINISHED_PARAM) === '1' || returnedCallback?.google_attempt === true;

  // Clerk can return here before the callback's poll runs. Observe that
  // outcome on the hydrated landing page, just as ProtectedRoute observes
  // success. Consuming the note prevents a retry from double-reporting it.
  useEffect(() => {
    if (!isLoaded || isSignedIn) return;
    const pending = consumePendingCallback(sessionStorageOrNull(), Date.now());
    if (!pending) return;
    setReturnedCallback(pending);
    failDashboardOAuthAttempt('returned_without_session');
    capture('auth_callback_failed', {
      method: 'oauth', flow: 'builtin_component',
      reason: 'returned_without_session', delivered_from: 'sign_in', ...pending,
    });
  }, [isLoaded, isSignedIn]);
  // Set by every sign-up CTA on an outreach audit report (signupCtaUrl). Named
  // here so the destination keeps the promise the button made; the import
  // itself happens after bootstrap in useSiteAuditClaim.
  const auditSite = useSiteAuditPreview(params.get('share'));

  log.info('[SignInPage] render', { isSignedIn, isLoaded, redirectUrl, initialEmail });

  // Stash a free-tool claim token (+source) the instant we land here, before the
  // post-auth navigate() or a Google OAuth full-page redirect drops the query
  // string. Consumed after bootstrap by useFreeScanClaim (App-level). Runs even
  // when the "already signed in" branch below redirects, so an existing-account
  // visitor who clicks the guest CTA still attaches their scan. OVE-4.
  useEffect(() => {
    captureFreeScanClaimParams(window.location.search);
    captureSiteAuditClaimParams(window.location.search);
    captureFreeToolSite(window.location.search);
  }, []);

  // Fire `sign_in_page_viewed` once per mount. Anonymous events here merge
  // into the user's profile after Clerk signs them in via $anon_distinct_id.
  //
  // Waits for Clerk to LOAD: `isSignedIn` is false until it does, so firing on
  // the first render counted every signed-in bounce through this page as a
  // sign-in page view (3 of the 11 views on 2026-10-09 were such bounces).
  const viewedRef = useRef(false);
  useEffect(() => {
    if (viewedRef.current || !isLoaded || isSignedIn) return;
    viewedRef.current = true;

    // Read marketing attribution cookie before it expires.
    const attr = consumeAttribution();

    capture('sign_in_page_viewed', {
      has_prefilled_email: Boolean(initialEmail),
      has_redirect: Boolean(redirectUrl),
      ...(attr ? { referrer_source: attr.source, referrer_path: attr.path } : {}),
    });
  }, [isLoaded, isSignedIn, initialEmail, redirectUrl]);

  // Already signed in — skip the sign-in form entirely
  if (isLoaded && isSignedIn) {
    const target = redirectUrl ?? '/dashboard';
    log.info('[SignInPage] Already signed in, redirecting to', { target });
    return <Navigate to={target} replace />;
  }

  return (
    <AuthShell>
      {auditSite && (
        <div className="auth-card__notice auth-card__notice--info mb-4 text-left" role="status">
          The accessibility findings from your {auditSite} report will open here as tracked&nbsp;issues.
        </div>
      )}
      {inviteDestination && (
        <div className="auth-card__notice auth-card__notice--info mb-4 text-left" role="status">
          Sign in as {initialEmail} to accept your team&nbsp;invitation.
        </div>
      )}
      {sessionExpired && (
        <div className="auth-card__notice mb-4 text-left" role="status">
          Your session expired. Sign in to&nbsp;continue.
        </div>
      )}
      {googleUnfinished && (
        <div className="auth-card__notice mb-4 text-left" role="alert">
          Google sign-in didn&rsquo;t finish. Sign in with your email below, or try Google&nbsp;again.
        </div>
      )}
      {returnedCallback && !googleUnfinished && (
        <div className="auth-card__notice mb-4 text-left" role="alert">
          Sign-in didn&rsquo;t finish. Try signing in again to continue.
        </div>
      )}
      <AuthCard afterAuthUrl={redirectUrl} initialEmail={initialEmail} initialView={params.get('recovery') === '1' ? 'forgot-password' : 'main'} />
    </AuthShell>
  );
}
